import { useSelector } from 'react-redux';
import { FieldType } from '../component/Form/SimpleForm';
import * as Booking from '../constants/Booking';
import I18n from '../i18n';

export const makeCustomizedTitles = (customizedFields = {}) => {
  return {
    [Booking.FieldNames.location]: customizedFields[Booking.FieldNames.location]?.title || I18n.t(['BOOKING', 'TITLE_SHOP']),
    [Booking.FieldNames.date]: customizedFields[Booking.FieldNames.date]?.title || I18n.t(['BOOKING', 'TITLE_DATE']),
    [Booking.FieldNames.timeslot]: customizedFields[Booking.FieldNames.timeslot]?.title || I18n.t(['BOOKING', 'TITLE_TIME']),
    [Booking.FieldNames.numberOfPeople]: customizedFields[Booking.FieldNames.numberOfPeople]?.title || I18n.t(['BOOKING', 'TITLE_PEOPLE']),
    [Booking.FieldNames.name]: customizedFields[Booking.FieldNames.name]?.title || I18n.t(['BOOKING', 'TITLE_NAME']),
    [Booking.FieldNames.phone]: customizedFields[Booking.FieldNames.phone]?.title || I18n.t(['BOOKING', 'TITLE_PHONE']),
    [Booking.FieldNames.remark]: customizedFields[Booking.FieldNames.remark]?.title || I18n.t(['BOOKING', 'TITLE_REMARKS']),
  };
};

export const useBookingCustomizedTitles = () => {
  const customizedFields = useSelector(state => state.booking.customizedFields);
  const titles = makeCustomizedTitles(customizedFields);

  return titles;
};

export function generateFields(bookingItem, isNameEditable = true, isPhoneEditable = true, customizedFields = {}, timeslotGroupOn = false, timeslotGroupData = undefined) {
    let fields = [];

    if (typeof bookingItem !== 'object' && Object.keys(bookingItem).length === 0) return fields;

    const customizedTitles = makeCustomizedTitles(customizedFields);

    if (bookingItem.branches) {
        fields.push({
            title: customizedTitles[Booking.FieldNames.location],
            name: Booking.FieldNames.location,
            fieldType: FieldType.selectModal,
            icon: require('../assets/pin.png'),
            required: true,
        });
    }

    fields.push({
        title: customizedTitles[Booking.FieldNames.date],
        name: Booking.FieldNames.date,
        fieldType: FieldType.calendar,
        icon: require('../assets/date.png'),
        required: true,
        date: bookingItem.date,
        footerMessage: timeslotGroupData?.dateMessage || '',
    });

    if (timeslotGroupOn) {
        fields.push({
            title: customizedTitles[Booking.FieldNames.timeslot],
            name: Booking.FieldNames.timeslot,
            fieldType: FieldType.timeslotGroup,
            icon: require('../assets/clock.png'),
            required: true,
            timeslotGroupData,
            use24HourFormat: false,
        });
    } else if (bookingItem.supportTimeslotSelect) {
        fields.push({
            title: customizedTitles[Booking.FieldNames.timeslot],
            name: Booking.FieldNames.timeslot,
            fieldType: FieldType.selectModal,
            icon: require('../assets/clock.png'),
            required: true,
        });
    }

    if (bookingItem.numberOfPeople) {
        fields.push({
            title: customizedTitles[Booking.FieldNames.numberOfPeople],
            name: Booking.FieldNames.numberOfPeople,
            fieldType: FieldType.bubble,
            icon: require('../assets/ppl.png'),
            required: true,
        });
    }

    fields.push({
        title: customizedTitles[Booking.FieldNames.name],
        name: Booking.FieldNames.name,
        fieldType: FieldType.text,
        icon: require('../assets/name.png'),
        required: true,
        editable: isNameEditable,
    });

    fields.push({
        title: customizedTitles[Booking.FieldNames.phone],
        name: Booking.FieldNames.phone,
        fieldType: FieldType.text,
        icon: require('../assets/phone-call.png'),
        required: true,
        keyboardType: 'phone-pad',
        editable: isPhoneEditable,
    });

    if (bookingItem.questions && Array.isArray(bookingItem.questions)) {
        bookingItem.questions.forEach(q => {
            if (q.type === Booking.QuestionType.text) {
                fields.push({
                    title: q.title,
                    name: Booking.FieldNames.question + q.id,
                    fieldType: FieldType.text,
                    required: q.required,
                    placeholder: q.placeholder,
                });
            } else if (q.type === Booking.QuestionType.select && q.options.length < 3) {
                fields.push({
                    title: q.title,
                    name: Booking.FieldNames.question + q.id,
                    fieldType: FieldType.bubble,
                    required: q.required,
                })
            } else if (q.type === Booking.QuestionType.select) {
                fields.push({
                    title: q.title,
                    name: Booking.FieldNames.question + q.id,
                    fieldType: FieldType.selectModal,
                    required: q.required,
                })
            } else if (q.type === Booking.QuestionType.checkbox) {
                fields.push({
                    title: q.title,
                    name: Booking.FieldNames.question + q.id,
                    fieldType: FieldType.bubble,
                    required: q.required,
                    multiple: true,
                })
            } else if (q.type === Booking.QuestionType.radio) {
                fields.push({
                    title: q.title,
                    name: Booking.FieldNames.question + q.id,
                    fieldType: FieldType.bubble,
                    required: q.required,
                })
            }
        });
    }
    
    if (bookingItem.showRemarks) {
        fields.push({
            title: customizedTitles[Booking.FieldNames.remark],
            name: Booking.FieldNames.remark,
            fieldType: FieldType.text,
            icon: require('../assets/chat.png')
        })
    }

    return fields;
}

export function BookingQuestion(source) {
    let type = null;
    switch (source.type) {
        case 'text':
            type = Booking.QuestionType.text;
            break;
        case 'select':
            type = Booking.QuestionType.select;
            break;
        case 'checkbox':
            type = Booking.QuestionType.checkbox;
            break;
        case 'radio':
            type = Booking.QuestionType.radio;
            break;
    }

    return {
        source,
        id: source.id,
        title: source.question,
        options: source.answers,
        placeholder: source.hints,
        required: source.required,
        type,
    }

}

export function BookingCategory(source) {
    return {
        source,
        id: source.id,
        title: source.name,
    }
}

export function BookingItem(source, branchList) {
    let questions = null;
    if (Array.isArray(source.customQuestion)) {
        questions = source.customQuestion.map(e => BookingQuestion(e));
    }

    let branches = null;
    if (branchList && Array.isArray(branchList) && source.branches && Array.isArray(source.branches)) {
        branches = branchList.filter(e => source.branches.includes(e.id)).map(e => {
            return {
                id: e.id,
                name: e.label,
                address: e.address,
            }
        });
    }

    let numberOfPeople = null;
    if (Array.isArray(source.people)) {
        numberOfPeople = source.people.map(e => parseInt(e));
    }

    let content = null;
    if (typeof source.content === 'string') {
        content = source.content;
    }

    let banner = null;
    if (source.photo) {
        banner = { uri: source.photo };
    }

    return {
        source,
        questions,
        branches,
        id: source.id,
        title: source.name,
        start: source.startDate,
        end: source.endDate,
        banner,
        date: source.date,
        numberOfPeople,
        content,
        supportTimeslotSelect: source.timeslot,
        showRemarks: source.showRemarks === '1',
    }
}

export function BookingRecord(source, branchList, productList) {
    let _branchList = branchList;
    if (!Array.isArray(branchList)) {
        _branchList = [branchList];
    }

    let _productList = productList;
    if (!Array.isArray(productList)) {
        _productList = [productList];
    }

    const productDetail = _productList.find(i => i.id === source.productId);

    let shop = null;
    if (source.branch) {
        shop = _branchList.find(b => b.id === source.branch)?.label;
    }

    let questions = null;
    let answers = null;

    if (source.customQuestion && productDetail) {
        questions = source.customQuestion.map(e => productDetail.customQuestion.find(q => q.id === e.id).question);
        answers = source.customQuestion.map(e => e.answer);
    }

    let item = null;
    if (productDetail) {
        item = productDetail.name;
    }

    let banner = null;
    if (productDetail && productDetail.photo) {
        banner = { uri: productDetail.photo };
    }

    return {
        source,
        item,
        shop,
        questions,
        answers,
        id: source.checkCode,
        date: source.date,
        numberOfPeople: source.people,
        status: source.status,
        submitDatetime: source.submitDate,
        name: source.name,
        phone: source.phone,
        remark: source.remarks,
        qrcode: source.checkinQrcode,
        banner,
        messageFromShop: source.message,
        canCancel: !!source.canCancel,
    }
}

export const bookingSelecter = (state) => {
    const { sid } = state.common;
    return state.shops[sid]?.booking ?? {};
}

export const shopSelecter = (state) => {
    const { sid } = state.common;
    return state.shops[sid]?.shop ?? {};
}

export const memberIdSelector = (state) => {
    const { sid } = state.common;
    return state.common.memberIdList[sid] ?? '';
}